import { CommonModule } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule,
  MatDialogRef,
  MatDialogTitle,
} from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltip } from "@angular/material/tooltip";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { provideNativeDateAdapter } from "@angular/material/core";
import { OrganizationTin } from "@models/organizationTin.model";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ModalStateService } from "@services/modal-state.service";
import { TaxIdentificationNumberDetailsActions } from "@store/actions/tax-identification-number-details/tax-identification-number-details.actions";
import { selectOrgId } from "@store/selectors/organization-emr-details/organization-emr-details.selector";
import { selectIsSaveDisabled } from "@store/selectors/tax-identification-number-details/tax-identification-number-details.selectors";
import { Subscription } from "rxjs";

@Component({
  selector: "app-edit-add-tin-modal",
  imports: [
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDividerModule,
    MatSelectModule,
    MatDialogModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatTooltip,
    MatDatepickerModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: "./edit-add-tin-modal.component.html",
  styleUrl: "./edit-add-tin-modal.component.css",
})
export class EditAddTinModalComponent implements OnInit, AfterViewChecked, AfterViewInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<EditAddTinModalComponent>);
  readonly data = inject(MAT_DIALOG_DATA);
  private readonly store = inject(Store);
  readonly modalStateService = inject(ModalStateService);
  private readonly updates$ = inject(Actions);
  private readonly cdr = inject(ChangeDetectorRef);

  orgId$ = this.store.select(selectOrgId);
  orgId = toSignal(this.orgId$);
  isSaveDisabled$ = this.store.select(selectIsSaveDisabled);
  hasSaveButtonBeenPressed = false;

  subs: Subscription[] = [];

  editAddTinForm = new FormGroup({
    name: new FormControl(this.data.tin?.name ?? "", {
      validators: [Validators.required, Validators.maxLength(128)],
    }),
    tin: new FormControl(this.data.tin?.tin ?? "", {
      validators: [Validators.required, Validators.maxLength(11)],
    }),
    effectiveDate: new FormControl<Date | null>(
      this.parseDate(this.data.tin?.effectiveDate),
      { validators: [this.effectiveDateRequiredWithExpirationValidator] }
    ),
    expirationDate: new FormControl<Date | null>(
      this.parseDate(this.data.tin?.expirationDate),
      { validators: [this.endDateAfterStartValidator] }
    ),
  });

  ngOnInit(): void {
    this.modalStateService.setModalOpen(true);
    this.subs.push(
      this.updates$
        .pipe(
          ofType(
            TaxIdentificationNumberDetailsActions.addTINSuccess,
            TaxIdentificationNumberDetailsActions.editTINSuccess
          )
        )
        .subscribe(() => {
          this.dialogRef.close();
        })
    );
    this.subs.push(
      this.editAddTinForm.get("expirationDate")!.valueChanges.subscribe(() => {
        this.editAddTinForm.get("effectiveDate")!.updateValueAndValidity();
      })
    );
    this.subs.push(
      this.editAddTinForm.get("effectiveDate")!.valueChanges.subscribe(() => {
        this.editAddTinForm.get("expirationDate")!.updateValueAndValidity();
      })
    );
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  private parseDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const [year, month, day] = value.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private serializeDate(date: Date | null | undefined): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  submitTin() {
    if (this.hasSaveButtonBeenPressed || this.editAddTinForm.invalid) {
      return;
    }
    if (this.data.tin) {
      var updatedTin: OrganizationTin = {
        id: this.data.tin.id,
        organizationId: this.data.tin.orgId,
        name: this.editAddTinForm.get("name")?.value ?? "",
        tin: this.editAddTinForm.get("tin")?.value ?? "",
        effectiveDate: this.serializeDate(this.editAddTinForm.get("effectiveDate")?.value),
        expirationDate: this.serializeDate(this.editAddTinForm.get("expirationDate")?.value),
      };
      this.store.dispatch(
        TaxIdentificationNumberDetailsActions.editTIN({
          data: updatedTin,
        })
      );
    } else {
      var newTin: OrganizationTin = {
        organizationId: this.orgId() ?? "",
        name: this.editAddTinForm.get("name")?.value ?? "",
        tin: this.editAddTinForm.get("tin")?.value ?? "",
        effectiveDate: this.serializeDate(this.editAddTinForm.get("effectiveDate")?.value),
        expirationDate: this.serializeDate(this.editAddTinForm.get("expirationDate")?.value),
      };

      this.store.dispatch(
        TaxIdentificationNumberDetailsActions.addTINSave({
          data: newTin
        })
      );
    }

    setTimeout(() => {
      this.hasSaveButtonBeenPressed = false;
    }, 3000);
  }

  private effectiveDateRequiredWithExpirationValidator(control: AbstractControl): ValidationErrors | null {
    const expirationDate = control.parent?.get("expirationDate")?.value;
    if (expirationDate && !control.value) {
      return { effectiveDateRequired: true };
    }
    return null;
  }

  private endDateAfterStartValidator(control: AbstractControl): ValidationErrors | null {
    const parent = control.parent;
    if (!parent) return null;
    const effectiveDate: Date | null = parent.get("effectiveDate")?.value;
    const expirationDate: Date | null = control.value;
    if (effectiveDate && expirationDate && expirationDate < effectiveDate) {
      return { endBeforeStart: true };
    }
    return null;
  }

  ngOnDestroy(): void {
    this.modalStateService.setModalOpen(false);
    this.subs.forEach((sub) => {
      sub?.unsubscribe();
    });
  }
}
